import React from "react";
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  ContactPage as LeadsIcon,
  Contacts as ContactsIcon,
} from "@mui/icons-material";
import BusinessIcon from "@mui/icons-material/Business";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import GroupsIcon from "@mui/icons-material/Groups";
import TimelineIcon from "@mui/icons-material/Timeline";
import AssessmentIcon from "@mui/icons-material/Assessment";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import SellIcon from "@mui/icons-material/Sell";

const MenuList = ({ isDarkTheme, currentPath, handleMenuItemClick }) => {
  // Get menuList from localStorage
  const menuList = JSON.parse(localStorage.getItem("menuList") || "[]");

  // Color configuration for each menu
  const iconColors = {
    "MENU-001": "#3b82f6", // Blue for Dashboard
    "MENU-002": "#10b981", // Green for Sales Board
    "MENU-003": "#8b5cf6", // Purple for Users
    "MENU-004": "#f59e0b", // Orange for Leads
    "MENU-005": "#22c55e", // Green for Qualified
    "MENU-006": "#ef4444", // Red for Disqualified
    "MENU-007": "#eab308", // Gold for Closed Won
    "MENU-008": "#f87171", // Light Red for Closed Loss
    "MENU-009": "#06b6d4",  // Cyan for Contacts
    "MENU-010": "#4B5AB6",  // Indigo for Companies
    "MENU-011": "#f57c00",  // Orange for Opportunities
    "MENU-012": "#00796b",  // Teal for Quotations
    "MENU-013": "#7b1fa2",  // Purple for Tasks
    "MENU-014": "#c62828",  // Red for Meetings
    "MENU-015": "#0288d1",  // Blue for Activities
    "MENU-016": "#388e3c",  // Green for Reports
    "MENU-017": "#455a64",  // Blue-grey for Administration
  };

  // Menu configuration with colored icons
  const menuConfig = {
    "MENU-001": {
      icon: <DashboardIcon sx={{ color: iconColors["MENU-001"] }} />,
      path: "/dashboard",
      label: "Dashboard",
    },
    "MENU-002": {
      icon: <SellIcon sx={{ color: iconColors["MENU-002"] }} />,
      path: "/salesboard",
      label: "Sales Board",
    },
    "MENU-003": {
      icon: <PersonIcon sx={{ color: iconColors["MENU-003"] }} />,
      path: "/users",
      label: "Users",
    },
    "MENU-004": {
      icon: <LeadsIcon sx={{ color: iconColors["MENU-004"] }} />,
      path: "/leads",
      label: "Leads",
    },
    "MENU-009": {
      icon: <ContactsIcon sx={{ color: iconColors["MENU-009"] }} />,
      path: "/contacts",
      label: "Contacts",
    },
    "MENU-010": {
      icon: <BusinessIcon sx={{ color: iconColors["MENU-010"] }} />,
      path: "/companies",
      label: "Companies",
    },
    "MENU-011": {
      icon: <LightbulbOutlinedIcon sx={{ color: iconColors["MENU-011"] }} />,
      path: "/opportunities",
      label: "Opportunities",
    },
    "MENU-012": {
      icon: <RequestQuoteIcon sx={{ color: iconColors["MENU-012"] }} />,
      path: "/quotations",
      label: "Quotations",
    },
    "MENU-013": {
      icon: <TaskAltIcon sx={{ color: iconColors["MENU-013"] }} />,
      path: "/tasks",
      label: "Tasks",
    },
    "MENU-014": {
      icon: <GroupsIcon sx={{ color: iconColors["MENU-014"] }} />,
      path: "/meetings",
      label: "Meetings",
    },
    "MENU-015": {
      icon: <TimelineIcon sx={{ color: iconColors["MENU-015"] }} />,
      path: "/activities",
      label: "Activities",
    },
    "MENU-016": {
      icon: <AssessmentIcon sx={{ color: iconColors["MENU-016"] }} />,
      path: "/reports",
      label: "Reports",
    },
    "MENU-017": {
      icon: <AdminPanelSettingsIcon sx={{ color: iconColors["MENU-017"] }} />,
      path: "/administration",
      label: "Administration",
    },
  };

  return (
    <List sx={{ px: 1.2, pt: 1 }}>
      {menuList
        .filter(menu => menu.permission === 'yes')
        .map((menu) => {
          const config = menuConfig[menu.menuId];
          if (!config) return null;
          const isActive = currentPath === config.path;

          return (
            <ListItem
              key={menu.menuId}
              button
              onClick={() => handleMenuItemClick(config.path)}
              sx={{
                borderRadius: 2.5,
                mb: 0.65,
                border: isActive
                  ? `1px solid ${isDarkTheme ? 'rgba(96,165,250,0.45)' : 'rgba(59,130,246,0.35)'}`
                  : `1px solid ${isDarkTheme ? 'rgba(148,163,184,0.08)' : 'rgba(226,232,240,0.6)'}`,
                background: isActive
                  ? (isDarkTheme ? 'rgba(37,99,235,0.2)' : 'rgba(59,130,246,0.1)')
                  : 'transparent',
                '&:hover': {
                  backgroundColor: isDarkTheme 
                    ? 'rgba(148,163,184,0.14)' 
                    : 'rgba(15,23,42,0.06)',
                  '& .menu-icon': {
                    backgroundColor: `${iconColors[menu.menuId]}15`,
                    borderRadius: 1,
                    p: 0.5,
                    transition: 'all 0.2s ease'
                  }
                },
              }}
            >
              <ListItemIcon
                className="menu-icon"
                sx={{
                  minWidth: 40,
                  transition: 'all 0.2s ease',
                  ...(isActive ? {
                    backgroundColor: `${iconColors[menu.menuId]}20`,
                    borderRadius: 1,
                    p: 0.5,
                  } : {}),
                  '& svg': {
                    fontSize: '1.5rem'
                  }
                }}
              >
                {config.icon}  
              </ListItemIcon>
              <ListItemText
                primary={menu.menuName}
                sx={{
                  '& .MuiListItemText-primary': {
                    fontSize: '0.95rem',
                    fontWeight: isActive ? 700 : 500,
                    color: isDarkTheme ? '#fff' : '#0f172a',
                    letterSpacing: 0.1,
                  }
                }}
              />
            </ListItem>
          );
        })}
    </List>
  );
};

export default MenuList;
